/**
 * Per-Wayfarer-user settings synchronization.
 *
 * The authenticated server record is authoritative. A user-namespaced local
 * cache paints immediately and remains editable offline; explicit offline
 * changes are pushed when connectivity returns. The old device-global record
 * is migrated only once, to the first user who opens this release. After that
 * an owner marker prevents a second driver on the same head unit inheriting it.
 */

import * as authApi from '../auth/api';
import {
  DEFAULT_SETTINGS,
  mergeSettings,
  userSettings,
  type Settings,
  type UserSettings,
} from './settings';
import { select, store } from './store';

const CACHE_PREFIX = 'wayfarer.settings.user.';
const OWNER_KEY = 'wayfarer.settings.owner';
const SYNC_DELAY_MS = 500;
const INITIAL_FETCH_TIMEOUT_MS = 3_000;

interface CachedSettings {
  values: UserSettings;
  revision: number;
  updatedAt: string | null;
  dirty: boolean;
  /** True once this cache has been matched to a real server record. */
  initialized: boolean;
}

function valuesKey(values: UserSettings): string {
  return JSON.stringify(values);
}

function normalizedValues(raw: unknown): UserSettings {
  return userSettings(mergeSettings(DEFAULT_SETTINGS, raw));
}

function readOwner(): number | null {
  try {
    const value = Number(localStorage.getItem(OWNER_KEY));
    return Number.isInteger(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

function writeOwner(userId: number): void {
  try {
    localStorage.setItem(OWNER_KEY, String(userId));
  } catch {
    /* Server sync still works without the legacy-migration guard. */
  }
}

function readCache(userId: number): CachedSettings | null {
  try {
    const raw = localStorage.getItem(`${CACHE_PREFIX}${userId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedSettings>;
    if (!parsed.values || typeof parsed.values !== 'object') return null;
    return {
      values: normalizedValues(parsed.values),
      revision:
        typeof parsed.revision === 'number' && parsed.revision >= 0
          ? Math.floor(parsed.revision)
          : 0,
      updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : null,
      dirty: parsed.dirty === true,
      initialized: parsed.initialized === true,
    };
  } catch {
    return null;
  }
}

function writeCache(userId: number, settings: CachedSettings): void {
  try {
    localStorage.setItem(`${CACHE_PREFIX}${userId}`, JSON.stringify(settings));
  } catch {
    /* The server remains authoritative; only instant/offline loading is lost. */
  }
}

export class UserSettingsSync {
  private cache: CachedSettings;
  private ignoredValuesKey: string | null = null;
  private syncTimer: number | null = null;
  private syncing = false;
  private syncAgain = false;
  private stopped = false;
  private unsubscribe: (() => void) | null = null;

  constructor(private userId: number) {
    const cached = readCache(userId);
    if (cached) {
      this.cache = cached;
      return;
    }

    const owner = readOwner();
    const mayMigrateLegacy = owner === null || owner === userId;
    this.cache = {
      values: mayMigrateLegacy
        ? userSettings(store.get().settings)
        : userSettings(mergeSettings(DEFAULT_SETTINGS, {})),
      revision: 0,
      updatedAt: null,
      // This is a candidate bootstrap, not a known offline edit. Reconcile
      // must check the server before it is allowed to publish anything.
      dirty: false,
      initialized: false,
    };
  }

  async start(): Promise<void> {
    this.applyValues(this.cache.values);
    writeOwner(this.userId);
    writeCache(this.userId, this.cache);
    this.unsubscribe = select(
      (state) => state.settings,
      (settings) => this.onLocalSettingsChanged(settings),
      { immediate: false },
    );
    window.addEventListener('online', this.onOnline);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    await this.reconcile(AbortSignal.timeout(INITIAL_FETCH_TIMEOUT_MS));
  }

  stop(): void {
    this.stopped = true;
    if (this.syncTimer !== null) window.clearTimeout(this.syncTimer);
    this.unsubscribe?.();
    window.removeEventListener('online', this.onOnline);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  private applyValues(values: UserSettings): void {
    const next = normalizedValues(values);
    const current = userSettings(store.get().settings);
    if (valuesKey(current) === valuesKey(next)) return;
    this.ignoredValuesKey = valuesKey(next);
    store.patchSettings(next);
  }

  private onLocalSettingsChanged(settings: Settings): void {
    const values = userSettings(settings);
    const key = valuesKey(values);
    if (this.ignoredValuesKey === key) {
      this.ignoredValuesKey = null;
      return;
    }
    if (valuesKey(this.cache.values) === key) return;
    this.cache = { ...this.cache, values, dirty: true };
    writeCache(this.userId, this.cache);
    this.scheduleSync();
  }

  private onOnline = (): void => {
    void this.reconcile();
  };

  private onVisibilityChange = (): void => {
    if (!document.hidden) void this.reconcile();
  };

  private async reconcile(signal?: AbortSignal): Promise<void> {
    if (this.stopped) return;
    let server: authApi.UserSettingsRecord;
    try {
      server = (await authApi.fetchUserSettings(this.userId, signal)).settings;
    } catch {
      return;
    }

    if (server.values === null || server.revision === 0) {
      // No database record yet: publish the per-user bootstrap only after the
      // authenticated GET proves there is nothing authoritative to replace it.
      this.cache = { ...this.cache, revision: 0, dirty: true };
      writeCache(this.userId, this.cache);
      this.scheduleSync(0);
      return;
    }

    if (this.cache.dirty) {
      // A local edit made while offline is the driver's latest explicit act.
      this.cache = { ...this.cache, revision: server.revision };
      writeCache(this.userId, this.cache);
      this.scheduleSync(0);
      return;
    }

    this.cache = {
      values: normalizedValues(server.values),
      revision: server.revision,
      updatedAt: server.updatedAt,
      dirty: false,
      initialized: true,
    };
    writeCache(this.userId, this.cache);
    this.applyValues(this.cache.values);
  }

  private scheduleSync(delay = SYNC_DELAY_MS): void {
    if (this.stopped) return;
    if (this.syncTimer !== null) window.clearTimeout(this.syncTimer);
    this.syncTimer = window.setTimeout(() => {
      this.syncTimer = null;
      void this.flush();
    }, delay);
  }

  private async flush(): Promise<void> {
    if (this.stopped || !this.cache.dirty) return;
    if (this.syncing) {
      this.syncAgain = true;
      return;
    }
    this.syncing = true;
    try {
      do {
        this.syncAgain = false;
        const sentValues = this.cache.values;
        const sentKey = valuesKey(sentValues);
        try {
          const saved = (await authApi.saveUserSettings(this.userId, sentValues)).settings;
          const unchanged = valuesKey(this.cache.values) === sentKey;
          if (unchanged && saved.values) {
            this.cache = {
              values: normalizedValues(saved.values),
              revision: saved.revision,
              updatedAt: saved.updatedAt,
              dirty: false,
              initialized: true,
            };
            writeCache(this.userId, this.cache);
            this.applyValues(this.cache.values);
          } else {
            this.cache = {
              ...this.cache,
              revision: saved.revision,
              updatedAt: saved.updatedAt,
              dirty: true,
              initialized: true,
            };
            writeCache(this.userId, this.cache);
            this.syncAgain = true;
          }
        } catch {
          this.cache = { ...this.cache, dirty: true };
          writeCache(this.userId, this.cache);
          return;
        }
      } while (this.syncAgain && !this.stopped);
    } finally {
      this.syncing = false;
    }
  }
}
